import { useEffect, useState, useMemo } from 'react';
import { trackRender } from '../utils/debugRender';
import { Link } from 'react-router-dom';
import { usersApi } from '../services/api';
import toast from 'react-hot-toast';
import {
  TextInput,
  Button,
  Select,
  Pagination,
  Checkbox,
  Badge,
  Dropdown,
  DropdownItem,
  DropdownDivider
} from 'flowbite-react';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  CheckIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

export default function Users() {
  // Data state
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState(null);

  // Filter state
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);

  // Bulk selection state
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Export state
  const [exporting, setExporting] = useState(false);

  // 🔍 DEBUG: Track renders to identify infinite loops
  trackRender('Users', { loading, page, search, planFilter, statusFilter });

  // Load users when filters change
  useEffect(() => {
    loadUsers();
  }, [page]);

  // Load users with debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 1) {
        loadUsers();
      } else {
        setPage(1); // Reset to first page on search/filter change
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [search, planFilter, statusFilter]);

  async function loadUsers() {
    try {
      setLoading(true);
      const data = await usersApi.list({ page, search, limit: 50 });

      // 🔍 DEBUG: Log API response to verify field names
      console.log('🔍 Users API Response:', {
        totalUsers: data.users?.length,
        firstUser: data.users?.[0],
        pagination: data.pagination
      });

      // 🔍 DEBUG: Check specific fields for first 3 users
      if (data.users?.length > 0) {
        console.log('🔍 First 3 users field check:', data.users.slice(0, 3).map(u => ({
          userId: u.userId,
          email: u.email,
          tier: u.tier,
          subscriptionStatus: u.subscriptionStatus,
          lastSignIn: u.lastSignIn,
          hasAllFields: !!(u.tier && u.subscriptionStatus && u.lastSignIn)
        })));
      }

      setUsers(data.users || []);
      setPagination(data.pagination);
      setSelectedUsers(new Set()); // Clear selection on new data load
      setSelectAll(false);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }

  // Filter users client-side (since API doesn't support plan/status filters yet)
  const filteredUsers = useMemo(() => {
    if (!users) return [];

    return users.filter((user) => {
      // Plan filter
      if (planFilter !== 'all' && user.tier !== planFilter) {
        return false;
      }

      // Status filter
      if (statusFilter !== 'all') {
        const isComplete = user.profileComplete;
        if (statusFilter === 'complete' && !isComplete) return false;
        if (statusFilter === 'incomplete' && isComplete) return false;
      }

      return true;
    });
  }, [users, planFilter, statusFilter]);

  // Bulk selection handlers
  function handleSelectAll() {
    if (selectAll) {
      setSelectedUsers(new Set());
      setSelectAll(false);
    } else {
      setSelectedUsers(new Set(filteredUsers.map((u) => u.userId)));
      setSelectAll(true);
    }
  }

  function handleSelectUser(userId) {
    const newSelection = new Set(selectedUsers);
    if (newSelection.has(userId)) {
      newSelection.delete(userId);
    } else {
      newSelection.add(userId);
    }
    setSelectedUsers(newSelection);
    setSelectAll(newSelection.size === filteredUsers.length);
  }

  // Export to CSV
  async function handleExportCSV() {
    try {
      setExporting(true);

      // Prepare CSV data
      const headers = ['User ID', 'Name', 'Email', 'Plan', 'Created', 'Last Sign In', 'Data Entries', 'Status'];
      const rows = filteredUsers.map((user) => [
        user.userId,
        `${user.firstName} ${user.lastName}`,
        user.email,
        user.tier,
        user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A',
        user.lastSignIn ? new Date(user.lastSignIn).toLocaleDateString() : 'Never',
        user.dataEntries || 0,
        user.profileComplete ? 'Complete' : 'Incomplete',
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
      ].join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `peetrack-users-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Exported ${filteredUsers.length} users to CSV`);
    } catch (error) {
      console.error('Failed to export CSV:', error);
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  }

  // Bulk actions
  function handleBulkAction(action) {
    const count = selectedUsers.size;

    switch (action) {
      case 'export-selected':
        // Export only selected users
        toast.success(`Exporting ${count} selected users...`);
        // TODO: Implement selected export
        break;
      case 'delete':
        toast.error('Delete functionality not implemented');
        break;
      default:
        break;
    }
  }

  // Calculate total pages
  const totalPages = pagination ? Math.ceil(pagination.total / pagination.limit) : 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Users</h1>
          <div className="mt-2 flex items-center gap-3">
            <Badge color="gray" size="sm">
              {pagination?.total || 0} total users
            </Badge>
            <Badge color="info" size="sm">
              {filteredUsers.length} filtered
            </Badge>
            {selectedUsers.size > 0 && (
              <Badge color="success" size="sm">
                {selectedUsers.size} selected
              </Badge>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          <Button color="light" size="sm" onClick={loadUsers} disabled={loading}>
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            color="success"
            size="sm"
            onClick={handleExportCSV}
            disabled={exporting || filteredUsers.length === 0}
            isProcessing={exporting}
          >
            <ArrowDownTrayIcon className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="rounded-lg bg-white p-4 shadow dark:bg-gray-800">
        <div className="grid gap-4 md:grid-cols-12">
          {/* Search Input */}
          <div className="md:col-span-5">
            <TextInput
              icon={MagnifyingGlassIcon}
              placeholder="Search by email or name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sizing="md"
            />
          </div>

          {/* Plan Filter */}
          <div className="md:col-span-3">
            <Select
              icon={FunnelIcon}
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
              sizing="md"
            >
              <option value="all">All Plans</option>
              <option value="free">Free</option>
              <option value="premium">Premium</option>
            </Select>
          </div>

          {/* Status Filter */}
          <div className="md:col-span-4">
            <Select
              icon={FunnelIcon}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sizing="md"
            >
              <option value="all">All Statuses</option>
              <option value="complete">Complete Profile</option>
              <option value="incomplete">Incomplete Profile</option>
            </Select>
          </div>
        </div>

        {/* Active Filters Display */}
        {(planFilter !== 'all' || statusFilter !== 'all' || search) && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-500">Active filters:</span>
            {search && (
              <Badge color="info" size="sm">
                Search: {search}
                <button
                  onClick={() => setSearch('')}
                  className="ml-1 hover:text-red-500"
                >
                  ×
                </button>
              </Badge>
            )}
            {planFilter !== 'all' && (
              <Badge color="warning" size="sm">
                Plan: {planFilter}
                <button
                  onClick={() => setPlanFilter('all')}
                  className="ml-1 hover:text-red-500"
                >
                  ×
                </button>
              </Badge>
            )}
            {statusFilter !== 'all' && (
              <Badge color="purple" size="sm">
                Status: {statusFilter}
                <button
                  onClick={() => setStatusFilter('all')}
                  className="ml-1 hover:text-red-500"
                >
                  ×
                </button>
              </Badge>
            )}
            <Button
              color="light"
              size="xs"
              onClick={() => {
                setSearch('');
                setPlanFilter('all');
                setStatusFilter('all');
              }}
            >
              Clear all
            </Button>
          </div>
        )}
      </div>

      {/* Bulk Actions Bar */}
      {selectedUsers.size > 0 && (
        <div className="flex items-center justify-between rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
          <div className="flex items-center gap-2">
            <CheckIcon className="h-5 w-5 text-blue-600" />
            <span className="font-medium text-blue-900 dark:text-blue-100">
              {selectedUsers.size} user{selectedUsers.size !== 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="flex gap-2">
            <Dropdown label="Bulk Actions" size="sm" color="light">
              <DropdownItem onClick={() => handleBulkAction('export-selected')}>
                <ArrowDownTrayIcon className="mr-2 h-4 w-4" />
                Export Selected
              </DropdownItem>
              <DropdownDivider />
              <DropdownItem onClick={() => handleBulkAction('delete')} className="text-red-600">
                Delete Selected
              </DropdownItem>
            </Dropdown>
            <Button size="sm" color="light" onClick={() => setSelectedUsers(new Set())}>
              Clear Selection
            </Button>
          </div>
        </div>
      )}

      {/* Users Table */}
      {loading ? (
        <div className="flex items-center justify-center rounded-lg bg-white py-12 shadow dark:bg-gray-800">
          <div className="text-center">
            <div className="spinner mb-4"></div>
            <p className="text-sm text-gray-500">Loading users...</p>
          </div>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg bg-white p-12 shadow dark:bg-gray-800">
          <MagnifyingGlassIcon className="h-16 w-16 text-gray-300" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
            No users found
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            Try adjusting your search or filter criteria
          </p>
          <Button
            color="light"
            size="sm"
            className="mt-4"
            onClick={() => {
              setSearch('');
              setPlanFilter('all');
              setStatusFilter('all');
            }}
          >
            Clear Filters
          </Button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  {/* Checkbox Column */}
                  <th className="px-4 py-3">
                    <Checkbox
                      checked={selectAll}
                      onChange={handleSelectAll}
                      aria-label="Select all users"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                    Plan
                  </th>
                  <th className="hidden px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:table-cell">
                    Created
                  </th>
                  <th className="hidden px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 xl:table-cell">
                    Last Sign In
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                    Data Entries
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                {filteredUsers.map((user) => (
                  <tr key={user.userId} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    {/* Checkbox */}
                    <td className="px-4 py-4">
                      <Checkbox
                        checked={selectedUsers.has(user.userId)}
                        onChange={() => handleSelectUser(user.userId)}
                        aria-label={`Select ${user.email}`}
                      />
                    </td>

                    {/* User Info */}
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Plan Badge */}
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Badge
                          color={user.tier === 'premium' ? 'warning' : 'gray'}
                          size="sm"
                        >
                          {user.tier}
                        </Badge>
                        {user.subscriptionStatus === 'active' && (
                          <Badge
                            color="success"
                            size="sm"
                            title="Active Stripe subscription"
                          >
                            💳 Stripe
                          </Badge>
                        )}
                      </div>
                    </td>

                    {/* Created Date */}
                    <td className="hidden whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400 lg:table-cell">
                      {user.createdAt
                        ? new Date(user.createdAt).toLocaleDateString()
                        : 'N/A'}
                    </td>

                    {/* Last Sign In */}
                    <td className="hidden whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400 xl:table-cell">
                      {user.lastSignIn
                        ? new Date(user.lastSignIn).toLocaleDateString()
                        : 'Never'}
                    </td>

                    {/* Data Entries */}
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                      {user.dataEntries?.toLocaleString() || 0}
                    </td>

                    {/* Status Badge */}
                    <td className="whitespace-nowrap px-6 py-4">
                      <Badge
                        color={user.profileComplete ? 'success' : 'warning'}
                        size="sm"
                      >
                        {user.profileComplete ? 'Complete' : 'Incomplete'}
                      </Badge>
                    </td>

                    {/* Actions */}
                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                      <div className="flex items-center gap-3">
                        <Link
                          to={`/users/${user.userId}`}
                          className="font-medium text-cyan-600 hover:underline dark:text-cyan-500"
                        >
                          View Details
                        </Link>
                        <button
                          onClick={async () => {
                            try {
                              await usersApi.syncStripe(user.userId);
                              toast.success('Subscription synced with Stripe');
                              // Reload user data
                              await loadUsers();
                            } catch (error) {
                              toast.error(`Sync failed: ${error.message}`);
                            }
                          }}
                          className="text-gray-400 hover:text-cyan-600 dark:hover:text-cyan-500"
                          title="Sync with Stripe"
                          aria-label="Sync subscription with Stripe"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Flowbite Pagination */}
          {pagination && pagination.total > pagination.limit && (
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800 sm:px-6">
              <div className="flex flex-1 justify-between sm:hidden">
                <Button
                  color="light"
                  size="sm"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  color="light"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages}
                >
                  Next
                </Button>
              </div>

              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Showing{' '}
                    <span className="font-medium">
                      {(page - 1) * pagination.limit + 1}
                    </span>{' '}
                    to{' '}
                    <span className="font-medium">
                      {Math.min(page * pagination.limit, pagination.total)}
                    </span>{' '}
                    of <span className="font-medium">{pagination.total}</span> results
                  </p>
                </div>

                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  showIcons
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
